import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/components/theme-provider";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { User, Settings, Shield, LogOut, ChevronDown, Moon, Sun } from "lucide-react";

export function UserDropdown() {
  const [_location, setLocation] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { theme, setTheme } = useTheme();
  
  if (!user) return null;
  
  // Get user's initials for avatar
  const getInitials = () => {
    if (!user?.username) return "U";
    
    // If it's an email, use the first letter of the part before the @
    if (user.username.includes('@')) {
      return user.username.split('@')[0].charAt(0).toUpperCase();
    }
    
    return user.username.charAt(0).toUpperCase();
  };
  
  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Check if dark mode is active (either explicitly set to dark or system preference is dark)
  const isDarkMode = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  
  const handleThemeToggle = (checked: boolean) => {
    setTheme(checked ? "dark" : "light");
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative flex items-center gap-2 px-2 py-1 cursor-pointer">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
            {getInitials()}
          </div>
          <span className="hidden md:inline-block text-sm font-medium">
            {user.username?.split('@')[0] ?? "User"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => setLocation('/profile')}>
          <User className="h-4 w-4" />
          <span>My Account</span>
        </DropdownMenuItem>
        <div className="px-2 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {isDarkMode ? (
                <Moon className="mr-2 h-4 w-4" />
              ) : (
                <Sun className="mr-2 h-4 w-4" />
              )}
              <span className="text-sm">Dark Mode</span>
            </div>
            <Switch
              checked={isDarkMode}
              onCheckedChange={handleThemeToggle}
            />
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} disabled={logoutMutation.isPending}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>{logoutMutation.isPending ? "Logging out..." : "Logout"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}